/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';

import { useStyles } from 'features/app/components/HeaderUserProfile/styles';
import { ProfilePicture } from 'features/app/components/ProfilePicture';
import useAuth from 'features/auth/hooks/useAuth';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-down-icon.svg';
import { EditProfileModal } from 'features/users/components/EditProfileModal';
import { PartnerUser } from 'features/users/ts/PartnerUser';
import useUser from 'features/user/hooks/useUser';
import { useTranslation } from 'hooks/useTranslation';
import i18n from 'locales/i18n';
import { ChangeLanguageModal } from 'features/users/components/ChangeLanguageModal';

import { ReactComponent as FeatherIcon } from 'assets/icons/feather-edit.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/open-account-logout.svg';
import { ReactComponent as ChangeLanguageIcon } from 'assets/icons/metro-language.svg';


interface HeaderUserProfileProps {}

const findAnchorElement = () => document.getElementById('user-name_block');

const scrollListener = (e: Event) => {
  e.preventDefault();
  window.scrollTo(0, 0);
};

export const HeaderUserProfile: React.FC<HeaderUserProfileProps> = () => {
  const direction = i18n.dir()

  const classes = useStyles({ direction });
  const { onLogout } = useAuth();
  const { userFullName, userRolesTitle, user } = useUser();
  const { t } = useTranslation('app');


  const [menuShown, setMenuShown] = React.useState(false);
  const [editProfileModalShown, setEditProfileModalShown] = React.useState<boolean | string>(false);
  const [changeLanguageModalShown, setChangeLanguageModalShown] = React.useState<boolean | string>(false);

  const toggleMenu = React.useCallback(() => {
    const newValue = !menuShown;

    if (newValue) {
      window.addEventListener('scroll', scrollListener);
    } else {
      window.removeEventListener('scroll', scrollListener);
    }

    setMenuShown(newValue);
  }, [menuShown]);

  const handleLogoutClick = React.useCallback(() => {
    toggleMenu();
    onLogout();
  }, [toggleMenu, onLogout]);

  const handleEditProfileClick = React.useCallback(() => {
    setEditProfileModalShown('' + Date.now());
    toggleMenu();
  }, [toggleMenu]);

  const handleChangeLanguageClick = React.useCallback(() => {
    setChangeLanguageModalShown('' + Date.now());
    toggleMenu();
  }, [toggleMenu]);

  const handleEditModalClose = React.useCallback(() => {
    setEditProfileModalShown(false);
  }, []);

  const handleChangeLanguageClose = React.useCallback(() => {
    setChangeLanguageModalShown(false);
  }, []);

  return (
    <Box className={classes.root}>
      <EditProfileModal
        open={!!editProfileModalShown}
        key={editProfileModalShown as string}
        user={user as unknown as PartnerUser}
        onClose={handleEditModalClose}
      />
      <ChangeLanguageModal
        open={!!changeLanguageModalShown}
        key={direction as string}
        onClose={handleChangeLanguageClose}
      />
      <ProfilePicture />
      <Box dir="row" className={classes.nameBlock}>
        <Box dir="column">
          <Box flexDirection="row" display="flex" justifyContent="space-between">
            <Typography variant="h4" className={classes.userName} id="user-name_block">
              {userFullName}
            </Typography>
            <IconButton onClick={toggleMenu} className={classes.arrowButton}>
              <ArrowIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" className={classes.userRoles}>
            {userRolesTitle}
          </Typography>
        </Box>

        <Menu
          open={menuShown}
          onClose={toggleMenu}
          keepMounted
          anchorEl={findAnchorElement as unknown as Element}
          disableScrollLock={true}
          classes={{
            paper: classes.menu,
          }}
        >
          <MenuItem onClick={handleEditProfileClick}>
            <div className={classes.menuItemIcon}>
              <FeatherIcon />
            </div>
            <Typography variant="body2" className={classes.menuItemText}>
              {t("editProfile")}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleChangeLanguageClick}>
            <div className={classes.menuItemIcon}>
              <ChangeLanguageIcon />
            </div>
            <Typography variant="body2" className={classes.menuItemText}>
              {t("changeLanguage")}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <div className={classes.menuItemIcon}>
              <LogoutIcon />
            </div>
            <Typography variant="body2" className={classes.menuItemText}>
              {t("logout")}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};
